import * as React from 'react';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { useMediaQuery } from '@noor/lib';
import { Tag } from './tag';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { OptionsPanel } from './options-panel';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';
import { usePanelDisclosure, MultiSelectDock } from './multi-select-group';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';

/** A selectable option in a {@link MultiSelect}. */
export interface MultiSelectOption {
  label: string;
  value: string;
  /** Secondary grey line shown under the label in the docked panel. */
  description?: string;
  /** Renders the row non-selectable (matches the Figma "disabled" checkbox). */
  disabled?: boolean;
}

export interface MultiSelectProps {
  /** Currently selected option values (controlled). */
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectOption[];
  /** Shown in the trigger when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  /**
   * Show the search box above the dropdown list. Defaults to `true`; set to
   * `false` for short fixed option sets (e.g. merchant verticals) where
   * filtering only adds chrome.
   */
  searchable?: boolean;
  emptyText?: string;
  disabled?: boolean;
  /** Trigger id, wired by `FormField.MultiSelect` for label association. */
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /**
   * Portal the popover to the body. Defaults to `false` so it works inside a
   * `Dialog` (see {@link PopoverContent} — a portaled popover can't be
   * wheel-scrolled through the dialog's scroll-lock).
   */
  portal?: boolean;
  className?: string;
  onBlur?: () => void;
  /**
   * Open the options as a card docked beside the enclosing modal (Figma
   * 358-12445) instead of an anchored dropdown. The dock needs room for a
   * second 480px column, so below `md` it falls back to the dropdown.
   */
  panel?: boolean;
  /** Header toggle label ("Выбрать все"), shown in the docked panel and above
   * the dropdown list. */
  selectAllLabel?: string;
  /**
   * Collapse the selection into a single count line in the trigger — the
   * design's «36 из 43 филиалов» (Figma 838-15908) — instead of one removable
   * chip per option. Receives the selected and total counts.
   *
   * Opt-in: without it the trigger keeps the chip list, which is what the
   * narrower selects (tags, promotion branches) still want.
   */
  summaryLabel?: (selected: number, total: number) => string;
  /**
   * Render a remove button on every chip. Defaults to `true`; pass `false`
   * for a Figma-style chipless-× trigger where removal happens only through
   * the dropdown checkboxes.
   */
  removableChips?: boolean;
}

/**
 * Controlled multi-select combobox: a bordered field that shows the selected
 * options as removable chips and opens a searchable checkbox list (Figma
 * 345-9828). State is fully controlled via `value`/`onChange` so it drops into
 * RHF (see `FormField.MultiSelect`) or any parent.
 */
export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  searchable = true,
  emptyText = 'No results.',
  disabled = false,
  id,
  portal = false,
  className,
  onBlur,
  panel = false,
  selectAllLabel,
  summaryLabel,
  removableChips = true,
  ...aria
}: MultiSelectProps) {
  // One id per instance, so a group can tell sibling panels apart.
  const panelId = React.useId();
  const { open, setOpen } = usePanelDisclosure(panelId);
  // The docked panel needs a second 480px column beside the modal; on narrower
  // viewports we fall back to the anchored dropdown.
  const wideEnoughForPanel = useMediaQuery('(min-width: 1040px)');
  const dockPanel = panel && wideEnoughForPanel;

  // Dismiss the docked panel on an outside pointer-down or Escape. The dropdown
  // variant gets both from Radix; the dock is a plain card, so it wires its own.
  const dockRef = React.useRef<HTMLDivElement>(null);
  const fieldRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!dockPanel || !open) return;

    function onPointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (dockRef.current?.contains(target) || fieldRef.current?.contains(target)) return;
      setOpen(false);
      onBlur?.();
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      // Close the panel without letting the key reach the enclosing dialog,
      // which would otherwise dismiss the whole modal in the same keystroke.
      event.stopPropagation();
      setOpen(false);
      onBlur?.();
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [dockPanel, open, setOpen, onBlur]);

  const selectedOptions = React.useMemo(
    () => value.map((v) => options.find((o) => o.value === v)).filter(isOption),
    [value, options],
  );

  function toggle(optionValue: string): void {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  }

  function remove(optionValue: string): void {
    onChange(value.filter((v) => v !== optionValue));
  }

  // "Выбрать все" reads as checked only when every selectable row is; ticking
  // it off clears the selection outright.
  const selectable = React.useMemo(
    () => options.filter((option) => !option.disabled),
    [options],
  );
  const allSelected =
    selectable.length > 0 && selectable.every((option) => value.includes(option.value));

  function toggleAll(): void {
    onChange(allSelected ? [] : selectable.map((option) => option.value));
  }

  const hasValue = selectedOptions.length > 0;

  function toggleOpen(): void {
    const next = !open;
    setOpen(next);
    if (!next) onBlur?.();
  }

  // The chevron is the focusable trigger (so a chip's remove button is never
  // nested inside a button), but the whole field is clickable — see `field`
  // below. In dropdown mode Radix wires this as the popover trigger; in docked
  // mode it toggles the panel directly.
  const toggleButton = (
    <button
      type="button"
      id={id}
      disabled={disabled}
      aria-invalid={aria['aria-invalid']}
      aria-describedby={aria['aria-describedby']}
      aria-expanded={open}
      aria-label={placeholder}
      data-slot="multi-select-toggle"
      {...(dockPanel ? { onClick: toggleOpen } : {})}
      className="-mr-1 flex shrink-0 self-start rounded-full p-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed"
    >
      <ChevronDownIcon
        className={cn(
          'size-5 text-muted-foreground transition-transform duration-200',
          open && 'rotate-180',
        )}
      />
    </button>
  );

  /**
   * Clicking anywhere on the field opens the list — the whole pill reads as one
   * control, and hunting for the chevron is the interaction users complained
   * about.
   *
   * Two origins are excluded: a chip (removing a selection must never toggle
   * the panel) and the chevron itself (which already owns the toggle — in
   * dropdown mode as the Radix trigger, in docked mode via its own `onClick`).
   *
   * Handled on `click`, not `pointerdown`, and without `preventDefault()`:
   * suppressing the default pointer behaviour inside a Radix `Dialog` swallows
   * the focus it needs to keep, and the dialog then reads the interaction as
   * outside itself and closes.
   */
  function handleFieldClick(event: React.MouseEvent<HTMLDivElement>): void {
    if (disabled) return;
    const target = event.target as HTMLElement;
    if (
      target.closest('[data-slot="tag"]') ||
      target.closest('[data-multi-select-chip]') ||
      target.closest('[data-slot="multi-select-toggle"]')
    ) {
      return;
    }
    toggleOpen();
  }

  const field = (
    <div
      ref={fieldRef}
      data-slot="multi-select-field"
      onClick={handleFieldClick}
      data-state={open ? 'open' : 'closed'}
      className={cn(
        // Figma 358-12114: 24px radius, 1px gray-200 border, 16px/10px
        // padding, x-small drop shadow. Metrics resolve through
        // `--multi-select-*` sizing handles.
        'flex min-h-[var(--multi-select-min-height)] w-full items-center gap-2 rounded-[var(--multi-select-radius)] border border-input bg-card px-4 py-2 text-left shadow-xs transition-[color,box-shadow]',
        'focus-within:border-input-border-focus focus-within:ring-[3px] focus-within:ring-ring/50',
        // The whole pill is the hit target, so it must read as one.
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        open && 'border-input-border-focus',
        aria['aria-invalid'] &&
          'border-input-border-invalid ring-input-border-invalid/20',
        className,
      )}
    >
      {/* Chips wrap, but the field never grows past two rows — beyond that the
       * list scrolls (2 × 28px chip + 8px gap). The chips sit in their own
       * scroll container rather than inside the toggle, so a chip's remove
       * button isn't nested in another button. */}
      <div
        className={cn(
          'flex flex-1 flex-wrap content-start items-center gap-2',
          hasValue && !summaryLabel && 'max-h-16 overflow-y-auto',
        )}
      >
        {summaryLabel ? (
          // The count line reads as filled-in text, not a placeholder, once
          // anything is selected — matching the design's black «36 из 43».
          <span
            className={cn(
              'text-sm font-medium tracking-tight',
              hasValue ? 'text-foreground' : 'text-input-placeholder',
            )}
          >
            {hasValue
              ? summaryLabel(selectedOptions.length, options.length)
              : placeholder}
          </span>
        ) : hasValue ? (
          <SelectedChips
            options={selectedOptions}
            disabled={disabled}
            removable={removableChips}
            onRemove={remove}
          />
        ) : (
          <span className="text-sm tracking-tight text-input-placeholder">
            {placeholder}
          </span>
        )}
      </div>
      {dockPanel ? toggleButton : <PopoverTrigger asChild>{toggleButton}</PopoverTrigger>}
    </div>
  );

  // Docked mode (Figma 358-12699): the options render as their own card to the
  // right of the modal. The panel is anchored to the nearest positioned
  // ancestor — the dialog card — so the two read as one ~960px group.
  if (dockPanel) {
    return (
      <>
        {field}
        {/* Portalled into the group's dock (a slot pinned to the modal card)
         * rather than rendered here. The panel is positioned against the MODAL,
         * but the field sits deep inside a scrolling form — leaving the panel
         * in place made its geometry hostage to every wrapper between the two,
         * and two of those wrappers broke it outright: a `relative` field group
         * stole the positioning ancestor, and the form's `overflow-y-auto`
         * clipped it out of sight (`overflow-x` cannot stay `visible` when the
         * other axis scrolls). See {@link MultiSelectGroup}. */}
        <MultiSelectDock>
          {open ? (
            <div
              ref={dockRef}
              className={cn(
                // `h-full` tracks the modal's box; `max-h` is the backstop for
                // a modal that is itself taller than the screen, so the panel
                // never runs off the top/bottom and its list scrolls instead.
                'pointer-events-auto absolute top-0 -right-5 z-10 flex h-full max-h-[calc(100dvh-4rem)] w-[30rem] translate-x-full flex-col',
                // Slide + fade in so the panel doesn't snap into place.
                'motion-safe:animate-in motion-safe:duration-200 motion-safe:fade-in-0 motion-safe:slide-in-from-left-2',
              )}
            >
              <OptionsPanel
                value={value}
                onChange={onChange}
                options={options}
                aria-label={placeholder}
                className="max-h-full min-h-0"
                {...(selectAllLabel !== undefined ? { selectAllLabel } : {})}
              />
            </div>
          ) : null}
        </MultiSelectDock>
      </>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      {/* Anchor the list to the whole field, but trigger from the chevron. */}
      <PopoverAnchor asChild>{field}</PopoverAnchor>
      <PopoverContent
        portal={portal}
        align="start"
        sideOffset={6}
        // The field is the popover's anchor: without this, Radix closes on
        // pointer-down and the field's own click handler toggles it straight
        // back open, so closing takes several clicks. Suppress Radix's close
        // for field clicks and let the click toggle exactly once — except on
        // a chip remove, which keeps Radix's close-after-remove.
        onPointerDownOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (
            target &&
            fieldRef.current?.contains(target) &&
            !target.closest('[data-slot="tag"]')
          ) {
            event.preventDefault();
          }
        }}
        // Keep the list inside the viewport instead of overlapping the fields
        // below it; the list scrolls once it runs out of room.
        collisionPadding={12}
        // The anchor is the field, so its width (not the chevron trigger's)
        // sizes the list. Figma 838-16000: 23px radius card, no inner padding —
        // the search sits in its own 12px inset and the "select all" strip runs
        // edge to edge.
        //
        // Bounding the CARD (not the inner list) is what keeps the dropdown
        // inside the modal: the search pill and the "select all" strip keep
        // their natural height and only the option list scrolls, so a long
        // branch list can never push the control past the dialog's edge.
        className="flex max-h-[min(22rem,var(--radix-popover-content-available-height))] w-(--radix-popover-trigger-width) min-w-56 flex-col overflow-hidden rounded-[23px] border border-input p-0 shadow-lg"
      >
        <Command className="flex min-h-0 flex-col">
          {/* Pill-shaped search (Figma 838-16001): 99px radius, gray-200
           * border, its own shadow, inset 8px from the card edge. Hidden for
           * short fixed option sets via `searchable={false}`. */}
          {searchable ? (
            <div className="p-2 pb-0">
              <CommandInput
                placeholder={searchPlaceholder}
                className="h-auto py-0"
                wrapperClassName="h-10 gap-2 rounded-[99px] border border-input border-b-input px-3 shadow-xs [&_svg]:size-5 [&_svg]:opacity-100 [&_svg]:text-muted-foreground"
              />
            </div>
          ) : null}
          {/* "Выбрать все" strip: gray-50 fill, gray-100 hairline, full-bleed. */}
          {selectAllLabel ? (
            <button
              type="button"
              onClick={toggleAll}
              aria-pressed={allSelected}
              className="mt-3 flex w-full cursor-pointer items-center gap-2 border-y border-muted bg-muted/50 px-3 py-1.5 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <OptionCheckbox checked={allSelected} />
              <span className="text-base font-medium tracking-tight text-foreground">
                {selectAllLabel}
              </span>
            </button>
          ) : null}
          <CommandList className="min-h-0 flex-1 p-2">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    disabled={option.disabled ?? false}
                    onSelect={() => toggle(option.value)}
                    className="gap-2 px-1 py-1.5"
                  >
                    <OptionCheckbox checked={isSelected} />
                    <span className="text-sm font-normal tracking-tight text-foreground">
                      {option.label}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The dropdown's checkbox (Figma 838-16006): 24px box, 8px radius, 2px
 * gray-300 border, filling solid with the orange primary when checked. The
 * check colour is pinned so it isn't inherited from `CommandItem`'s muted
 * icon rule.
 */
function OptionCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-[6px] border-2 transition-colors',
        checked
          ? 'border-control-bg-checked bg-control-bg-checked'
          : 'border-control-border bg-transparent',
      )}
    >
      {checked ? <CheckIcon className="size-3.5 text-control-fg-checked!" /> : null}
    </span>
  );
}

function SelectedChips({
  options,
  disabled,
  removable,
  onRemove,
}: {
  options: MultiSelectOption[];
  disabled: boolean;
  removable: boolean;
  onRemove: (value: string) => void;
}) {
  const visible = options.slice(0, 2);
  const hidden = options.slice(2);
  const chipClassName = 'h-7 gap-1 py-0 pr-1.5 pl-2.5 text-sm tracking-tight';
  const canRemove = removable && !disabled;

  return (
    <>
      {visible.map((option) => (
        <Tag
          key={option.value}
          variant="secondary"
          data-multi-select-chip
          // Figma badge: 28px tall, gray-100 fill, 14px label.
          className={chipClassName}
          {...(canRemove ? { onRemove: () => onRemove(option.value) } : {})}
        >
          {option.label}
        </Tag>
      ))}
      {hidden.length > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Tag
              variant="secondary"
              tabIndex={0}
              data-multi-select-chip
              aria-label={hidden.map((option) => option.label).join(', ')}
              className="h-7 gap-1 px-2.5 py-0 text-sm tracking-tight"
            >
              +{hidden.length}
            </Tag>
          </TooltipTrigger>
          <TooltipContent className="max-w-64">
            <span className="block whitespace-normal">
              {hidden.map((option) => option.label).join(', ')}
            </span>
          </TooltipContent>
        </Tooltip>
      ) : null}
    </>
  );
}

/** Type guard: drop `undefined`s left by a stale selected value. */
function isOption(option: MultiSelectOption | undefined): option is MultiSelectOption {
  return option !== undefined;
}
